import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { publicApi, currency } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/lib/content";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";

export default function Cart() {
  const navigate = useNavigate();
  const { items, updateItem, removeItem, clearCart, subtotal } = useCart();
  const { user, login } = useAuth();
  const { t } = useContent();
  const [form, setForm] = useState({ event_start: "", event_end: "", delivery_address: "", contact_phone: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [delivery, setDelivery] = useState(null);

  useEffect(() => {
    if (items.length === 0 || !form.delivery_address.trim()) { setDelivery(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await publicApi.post("/delivery/quote", { address: form.delivery_address, subtotal });
        setDelivery(data);
      } catch { setDelivery(null); }
    }, 400);
    return () => clearTimeout(t);
  }, [form.delivery_address, subtotal, items.length]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const handleSignIn = () => { localStorage.setItem("post_login_redirect", "/cart"); login(); };

  const submit = async () => {
    if (!user) return handleSignIn();
    if (!form.event_start || !form.event_end || !form.delivery_address || !form.contact_phone) {
      toast.error("Please fill in event dates, address and phone.");
      return;
    }
    setSubmitting(true);
    try {
      const { data: order } = await publicApi.post("/orders", {
        items: items.map((i) => ({ product_id: i.product_id, name: i.name, price_per_day: i.price_per_day, quantity: i.quantity, days: i.days })),
        ...form,
      });
      clearCart();
      toast.success("Reservation created — redirecting to secure deposit payment…");
      const { data: pay } = await publicApi.post("/payments/checkout", {
        order_id: order.id,
        payment_type: "deposit",
        origin_url: window.location.origin,
      });
      window.location.href = pay.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit reservation.");
      setSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <ShoppingBag className="w-12 h-12 text-gold/50 mx-auto mb-6" />
        <h1 className="font-serif text-3xl text-cream">{t("cart.empty_title")}</h1>
        <p className="text-muted2 mt-3">{t("cart.empty_subtitle")}</p>
        <Link to="/catalog" className="btn-gold mt-8">{t("cart.empty_cta")}</Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <h1 className="font-serif text-4xl text-cream mb-10">{t("cart.title")}</h1>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-4">
          {items.map((i) => (
            <div key={i.product_id} data-testid={`cart-item-${i.product_id}`} className="gold-card !border-gold/20 p-4 flex gap-4 items-center">
              <img src={i.image} alt={i.name} className="w-24 h-24 rounded-xl object-cover" />
              <div className="flex-1 min-w-0">
                <h3 className="font-serif text-lg text-cream truncate">{i.name}</h3>
                <p className="text-gold text-sm">{currency(i.price_per_day)} / day</p>
                <div className="flex flex-wrap gap-4 mt-3 items-center">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted2">Qty</span>
                    <button onClick={() => updateItem(i.product_id, { quantity: Math.max(1, i.quantity - 1) })} className="w-7 h-7 rounded-full border border-gold/40 text-gold flex items-center justify-center"><Minus className="w-3 h-3" /></button>
                    <span className="w-6 text-center text-cream">{i.quantity}</span>
                    <button onClick={() => updateItem(i.product_id, { quantity: i.quantity + 1 })} className="w-7 h-7 rounded-full border border-gold/40 text-gold flex items-center justify-center"><Plus className="w-3 h-3" /></button>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted2">Days</span>
                    <button onClick={() => updateItem(i.product_id, { days: Math.max(1, i.days - 1) })} className="w-7 h-7 rounded-full border border-gold/40 text-gold flex items-center justify-center"><Minus className="w-3 h-3" /></button>
                    <span className="w-6 text-center text-cream">{i.days}</span>
                    <button onClick={() => updateItem(i.product_id, { days: i.days + 1 })} className="w-7 h-7 rounded-full border border-gold/40 text-gold flex items-center justify-center"><Plus className="w-3 h-3" /></button>
                  </div>
                </div>
              </div>
              <div className="text-right">
                <p className="text-gold-bright font-semibold">{currency(i.price_per_day * i.quantity * i.days)}</p>
                <button data-testid={`cart-remove-${i.product_id}`} onClick={() => removeItem(i.product_id)} className="mt-3 text-muted2 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>

        <div className="gold-card !border-gold/30 p-6 h-fit">
          <h2 className="font-serif text-2xl text-cream mb-6">{t("cart.details_title")}</h2>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted2 mb-1 block">Event start</label>
                <input type="date" data-testid="checkout-event-start" value={form.event_start} onChange={set("event_start")} className="w-full bg-ink border border-gold/25 rounded-lg px-3 py-2 text-cream text-sm focus:outline-none focus:border-gold" />
              </div>
              <div>
                <label className="text-xs text-muted2 mb-1 block">Event end</label>
                <input type="date" data-testid="checkout-event-end" value={form.event_end} onChange={set("event_end")} className="w-full bg-ink border border-gold/25 rounded-lg px-3 py-2 text-cream text-sm focus:outline-none focus:border-gold" />
              </div>
            </div>
            <div>
              <label className="text-xs text-muted2 mb-1 block">Delivery address</label>
              <input data-testid="checkout-address" value={form.delivery_address} onChange={set("delivery_address")} placeholder="Venue address" className="w-full bg-ink border border-gold/25 rounded-lg px-3 py-2 text-cream text-sm focus:outline-none focus:border-gold" />
            </div>
            <div>
              <label className="text-xs text-muted2 mb-1 block">Contact phone</label>
              <input data-testid="checkout-phone" value={form.contact_phone} onChange={set("contact_phone")} placeholder="Phone number" className="w-full bg-ink border border-gold/25 rounded-lg px-3 py-2 text-cream text-sm focus:outline-none focus:border-gold" />
            </div>
            <div>
              <label className="text-xs text-muted2 mb-1 block">Notes</label>
              <textarea data-testid="checkout-notes" value={form.notes} onChange={set("notes")} rows={3} placeholder="Setup instructions, timings…" className="w-full bg-ink border border-gold/25 rounded-lg px-3 py-2 text-cream text-sm focus:outline-none focus:border-gold resize-none" />
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-gold/20 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted2">Subtotal</span>
              <span data-testid="cart-subtotal" className="text-cream">{currency(subtotal)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted2">Delivery {delivery?.zone ? `· ${delivery.zone}` : ""}</span>
              <span data-testid="cart-delivery-fee" className="text-cream">
                {delivery ? (delivery.fee === 0 ? "Free" : currency(delivery.fee)) : "Enter address"}
              </span>
            </div>
            <div className="flex justify-between items-center pt-2 border-t border-gold/10">
              <span className="text-cream">Total</span>
              <span data-testid="cart-total" className="text-2xl font-serif text-gold">{currency(subtotal + (delivery?.fee || 0))}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted2">Due today (50% deposit)</span>
              <span data-testid="cart-deposit-due" className="text-gold-bright">{currency((subtotal + (delivery?.fee || 0)) / 2)}</span>
            </div>
          </div>

          <button data-testid="checkout-submit-reservation-button" onClick={submit} disabled={submitting} className="btn-gold w-full mt-6 disabled:opacity-60">
            {submitting ? "Submitting…" : user ? "Submit Reservation" : "Sign in to Reserve"}
          </button>
          <p className="text-xs text-muted2 text-center mt-3">{t("cart.footer_note")}</p>
        </div>
      </div>
    </div>
  );
}
